import { createApi } from "@reduxjs/toolkit/query/react";
import { User, UserFormData } from "./user.type";
import { CollectionData, PaginationResults, TypeQuery } from "@/lib/type";
import { baseQueryWithLogout } from "@/lib/baseQuery";

export const UserApi = createApi({
  reducerPath: "userApi",
  tagTypes: ["user"],
  baseQuery: baseQueryWithLogout,
  endpoints: (build) => ({
    getUsers: build.query<PaginationResults<User>, TypeQuery>({
      query: ({ restaurantId, franchiseId, ...query }) => ({
        url: restaurantId
          ? `restaurants/${restaurantId}/employees`
          : `restaurant_franchises/${franchiseId}/employees`,
        params: { ...query },
      }),
      providesTags: ["user"],
      transformResponse: (
        data: CollectionData<{ id: string; employeeUser: User }>
      ) => {
        return {
          // Keep the employee id: dismissal targets the employee record, not
          // the user account.
          results: data["hydra:member"]?.map((item) => ({
            ...item?.employeeUser,
            employeeId: item?.id,
          })),
          count: data["hydra:totalItems"],
        };
      },
    }),

    createOrUpdateUser: build.mutation<
      User,
      { id?: string; data: UserFormData | FormData; restaurantId?: string }
    >({
      query: ({ id, data, restaurantId }) => {
        if (id) {
          return {
            url: `users/${id}`,
            method: "PATCH",
            body: JSON.stringify(data),
            headers: {
              "Content-Type": "application/merge-patch+json",
            },
          };
        }
        return {
          url: "users",
          method: "POST",
          body: data,
          headers: {
            "content-type": "application/json",
            ...(restaurantId ? { "X-Restaurant-Id": restaurantId } : {}),
          },
        };
      },
      invalidatesTags: ["user"],
    }),

    deleteUser: build.mutation<User, string>({
      query: (id) => ({
        url: `users/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["user"],
    }),

    // Dismisses an employee (detaches them from the restaurant); their user
    // account and history remain. Primary manager / owner only.
    dismissEmployee: build.mutation<void, string>({
      query: (employeeId) => ({
        url: `employees/${employeeId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["user"],
    }),

    getCurrentUser: build.query<User, void>({
      query: () => "users/current",
    }),

    getRestaurantManagers: build.query<
      PaginationResults<User>,
      { restaurantId: string }
    >({
      query: ({ restaurantId }) => ({
        url: `restaurants/${restaurantId}/managers`,
      }),
      providesTags: ["user"],
      transformResponse: (data: CollectionData<{ manager: User }>) => ({
        results: data["hydra:member"]?.map((item) => item.manager) ?? [],
        count: data["hydra:totalItems"],
      }),
    }),

    // Self-service change of the login phone or email (SFAPI-207): the API
    // sends a code to the NEW identifier; nothing changes until it is
    // confirmed.
    requestIdentifierChange: build.mutation<
      void,
      { phone: string } | { email: string }
    >({
      query: (body) => ({
        url: "users/current/identifier_change",
        method: "POST",
        body,
      }),
    }),

    // Returns the updated current user (same shape as /users/current).
    confirmIdentifierChange: build.mutation<User, { code: string }>({
      query: (body) => ({
        url: "users/current/identifier_change/confirm",
        method: "POST",
        body,
      }),
      invalidatesTags: ["user"],
    }),

    setUserPin: build.mutation<void, { id: string; pin: string }>({
      query: ({ id, pin }) => ({
        url: `users/${id}/pin`,
        method: "POST",
        body: { pin },
      }),
    }),
  }),
});

export const {
  useGetUsersQuery,
  useLazyGetCurrentUserQuery,
  useGetCurrentUserQuery,
  useCreateOrUpdateUserMutation,
  useDeleteUserMutation,
  useDismissEmployeeMutation,
  useGetRestaurantManagersQuery,
  useSetUserPinMutation,
  useRequestIdentifierChangeMutation,
  useConfirmIdentifierChangeMutation,
} = UserApi;
